import api from '@/services/api';

import { paymentMethod as _paymentMethod } from '@/services/api/payment/payment';

const createGoCardlessBankAccount = (companyId, payload) => api()
  .post(`companies/${companyId}/bank_account`, payload);

const getGoCardlessBankAccounts = (companyId) => api()
  .get(`companies/${companyId}/bank_account`);

const deleteGoCardlessBankAccount = (companyId, accountId) => api()
  .delete(`bankaccounts/${accountId}`);

const activateGoCardlessBankAccount = (accountId) => api()
  .patch(`bankaccounts/${accountId}/activate`);

const payoutTransaction = (transactionId, body) => api().put(`transactions/${transactionId}/payout`, body);

const payoutConfirmation = (transactionId, body) => {
  if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
    console.error('Invalid transactionId in payoutConfirmation:', transactionId);
    throw new Error(`Invalid transactionId in payoutConfirmation: ${transactionId}`);
  }
  return api().put(`transactions/${transactionId}/confirm`, body);
};

async function addPaymentMethodIban(stripe, container, companyId, { customerName, contactEmail }) {
  const { paymentMethod } = await stripe.createPaymentMethod({
    type: 'sepa_debit',
    sepa_debit: container,
    billing_details: {
      name: customerName,
      email: contactEmail,
    },
  });
  const paymentMethodBody = {
    externalReference: paymentMethod.id,
    active: true,
  };
  // Call payment methods to check if an action is required
  const { data } = await _paymentMethod(companyId, paymentMethodBody);
  const { paymentMethod: { id }, clientSecretId, paymentStatus } = data;

  // launch 3DS secure to approve card
  if (paymentStatus === 'requires_action') {
    await stripe.confirmCardSetup(clientSecretId, {
      payment_method: { card: container },
    });
    await _paymentMethod(companyId, paymentMethodBody);
  }
  return id;
}

const addPaymentMethod = async (stripe, container, companyId) => {
  const { paymentMethod } = await stripe.createPaymentMethod({
    type: 'card',
    card: container,
  });
  const paymentMethodBody = {
    externalReference: paymentMethod.id,
    active: true,
  };
  let id = null;
  // Call payment methods to check if an action is required
  const { data } = await _paymentMethod(companyId, paymentMethodBody);
  const { clientSecretId, paymentStatus } = data;

  // launch 3DS secure to approve card
  if (paymentStatus === 'requires_action') {
    await stripe.confirmCardSetup(clientSecretId, {
      payment_method: { card: container },
    });
    const {
      data: { paymentMethod: { id: paymentMethodId } },
    } = await _paymentMethod(companyId, paymentMethodBody);
    id = paymentMethodId;
  } else {
    const { paymentMethod: { id: paymentMethodId } } = data;
    id = paymentMethodId;
  }
  return id;
};

const payment = async (stripe, container, companyId, transactionId, paymentMethod, paymentMethodType = 'CARD') => {
  const { id } = paymentMethod;
  const paymentMethodBody = {
    externalReference: id,
    active: true,
  };
  let paymentMethodId = null;

  // Call payment methods to check if an action is required
  const { data } = await _paymentMethod(companyId, paymentMethodBody);
  const {
    clientSecretId,
    paymentStatus,
  } = data;

  // launch 3DS secure to approve card
  if (paymentStatus === 'requires_action') {
    // Verify 3DS with Stripe
    await stripe.confirmCardSetup(clientSecretId, {
      payment_method: { card: container },
    });

    const {
      data: { paymentMethod: { id: newId } },
    } = await _paymentMethod(companyId, paymentMethodBody);
    paymentMethodId = newId;
  } else {
    paymentMethodId = data.paymentMethod.id;
  }

  // checkout transaction
  const {
    data: {
      paymentStatus: payoutStatus,
      clientSecretId: payoutSecretId,
    },
  } = await payoutTransaction(transactionId, { paymentMethodId, paymentMethodType });

  // verify if transaction needs 3DS secure
  if (payoutStatus === 'requires_action') {
    const {
      paymentIntent: { id: paymentIntentId },
    } = await stripe.confirmCardPayment(payoutSecretId);
    await payoutConfirmation(transactionId, { paymentMethodId, paymentIntentId });
  }
};

/**
 * Wrapper for iban
 * @param {*} stripe
 * @param {*} container
 * @param {*} companyId
 * @param {*} transactionId
 * @param {*} contactInformations
 * @returns a payment
 */
const paymentIban = async (wrapper, { customerName, contactEmail }) => {
  const { paymentMethod } = await wrapper[0].createPaymentMethod({
    type: 'sepa_debit',
    sepa_debit: wrapper[1],
    billing_details: {
      name: customerName,
      email: contactEmail,
    },
  });
  return payment(...wrapper, paymentMethod, 'IBAN');
};

/**
 * Wrapper for credit card payment
 * @param {*} stripe
 * @param {*} container
 * @param {*} companyId
 * @param {*} transactionId
 * @returns a payment
 */
const paymentCreditCard = async ([stripe, container, companyId, transactionId]) => {
  // add a creditCard
  const id = await addPaymentMethod(stripe, container, companyId);

  // checkout transaction
  const {
    data: {
      paymentStatus: payoutStatus,
      clientSecretId: payoutSecretId,
    },
  } = await payoutTransaction(transactionId, { paymentMethodId: id, paymentMethodType: 'CARD' });

  // verify if transaction needs 3DS secure
  if (payoutStatus === 'requires_action') {
    const {
      paymentIntent: { id: paymentIntentId },
    } = await stripe.confirmCardPayment(payoutSecretId);
    await payoutConfirmation(transactionId, { paymentMethodId: id, paymentIntentId });
  }
};

const isIbanValid = (iban) => api().get(`iban/${iban}`);
const getStripeToken = async (stripe, container) => {
  const successToken = await stripe.createToken(container)
    .catch(() => {});
  return successToken.token;
};

/**
 * GoCardless payment flow
 * @param {string} companyId
 * @param {string} transactionId
 * @param {string} iban
 * @param {Object} organization - The full organization/risk object
 * @returns {Promise} payment result
 */
const paymentGoCardless = async (companyId, transactionId, iban, organization) => {
  if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
    console.error('Invalid transactionId received:', transactionId);
    throw new Error(`Invalid transactionId received in paymentGoCardless: ${transactionId}`);
  }

  const { data: existingBankAccounts } = await getGoCardlessBankAccounts(companyId);
  let bankAccount = existingBankAccounts.find(account => account.active === true);

  if (!bankAccount) {
    // Only create a new account if we have an IBAN to work with
    if (!iban) {
      throw new Error('No active bank account found and no IBAN provided to create a new one');
    }

    const bankAccountPayload = {
      iban,
      active: true,
      organization,
    };

    const { data: newBankAccount } = await createGoCardlessBankAccount(companyId, bankAccountPayload);
    bankAccount = newBankAccount;
  }

  const { data } = await payoutTransaction(transactionId, {
    paymentMethodId: bankAccount.id,
    paymentMethodType: 'IBAN',
  });

  return data;
};

export {
  payment,
  paymentIban,
  payoutConfirmation,
  payoutTransaction,
  paymentCreditCard,
  addPaymentMethod,
  addPaymentMethodIban,
  isIbanValid,
  getStripeToken,
  createGoCardlessBankAccount,
  getGoCardlessBankAccounts,
  deleteGoCardlessBankAccount,
  activateGoCardlessBankAccount,
  paymentGoCardless,
};
